<template>
  <div class="others-tab-main scrollbar" v-loader.opacity.tab="loading">
    
    <div v-if="allExamOthersData.length > 0">
      <div class="section-gallery">
        <lightgallery-vue 
          ref="lGallery" 
          id="lightgallery"
          :settings="lightGallerySettings"
        >
          <template v-for="(item, index) in examOthersMediaData" :key="index">
            
            <a v-if="isSupportedVideo(item)"
               class="lg-item" 
               :id="`lg-item-${item.id}`"
               data-lg-size="1280-720"
               :data-video="getVideoSource(item)" 
               :data-sub-html="`<h4>${item.originalName}</h4>`">
            </a>
            
            <a v-else-if="isSupportedImage(item)"
               class="lg-item" 
               :id="`lg-item-${item.id}`"
               :data-src="getMediaSource(item)" 
               :data-sub-html="`<h4>${item.originalName}</h4>`">
            </a>
          </template>
        </lightgallery-vue>
      </div>

      <BaseTable
        class="no-border others-table"
        :tabledata="othersTableData"
        @item-opened="handleRowClick"
      >
        <template v-slot:column_icon="{ item }">
          <div class="file-icon-cell">
            <i :class="$utils.getFileIcon(item.type)"></i>
          </div>
        </template>

        <template v-slot:column_actions="{ item }">
          <div class="action-group">
            <button class="btn btn-outline-secondary" type="button" name="button" 
              :data-title="$t('view')" data-test="`btnView`"
              @click.stop="handlePreview(item)">
              <IconEye />
            </button>

            <button class="btn btn-outline-secondary" type="button" name="button"
              :data-title="$t('tooltips.download')" data-test="`btnDownload`" 
              @click.stop="doDownload(item)">
              <IconDownload />
            </button>

            <button v-if="isExamDataOthersRemoveAllowed(item)" class="btn btn-outline-secondary" 
              type="button" name="button" :data-title="$t('remove')" data-test="btnRemove" 
              @click.stop="openOffcanvasDelete(item.id)">
              <IconTrash />
            </button>
          </div>
        </template>
      </BaseTable>
    </div>

    <div class="no-results-box" v-else-if="!loading && !allExamOthersData.length">
      <BaseNoResults :title="$t('exam.noFilesFound')" :text="$t('exam.uploadInstruction')"/>
    </div>

    <teleport to="body">
      <BaseGenericOffcanvas :offcanvas_id="'offcanvasDeleteGenericOthers'" ref="offcanvasGeneric"/>
      <BaseFilePreviewModal ref="baseFilePreviewModal" />
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.others-tab-main {
  padding:                              10px;
  flex:                                 1;
  display:                              flex;
  flex-direction:                       column;
  min-height:                           0;
  width:                                100%;
  overflow-y:                           auto;
  overflow-x:                           hidden;

  .section-gallery {
    display:                            none;
  }

  .file-icon-cell {
    i {
      color:                            var(--Brand-Primary);
      font-size:                        24px;
    }
  }

  .action-group {
    display:                            flex;
    justify-content:                    flex-end;
    gap:                                10px;

    .btn {
      background:                       var(--Shades-BG);

      &:hover,
      &:focus,
      &:active,
      &.active {
        color:                          var(--Brand-Primary);
        background:                     var(--Shades-10);
      }
    }
  }

  :deep(.others-table .action-group *:last-of-type[data-title]:after) {
    left:                               auto !important;
    right:                              0 !important;
    white-space:                        nowrap !important; 
  }
}

:deep(.others-table thead) {
  display:                              none !important;
}

:deep(.vtable-mob),
:deep(.table-responsive.desk) {
  overflow:                             visible !important;
  height:                               auto !important;
  max-height:                           none !important;
  flex:                                 none !important; 
}

@media (max-width: 991px) {
  .others-tab-main {
    :deep(.vtable-mob) {
      .inside-box-mob-item {
        display:                        flex !important;
        flex-wrap:                      wrap !important;
        align-items:                    center !important;

        p:nth-child(1) {
          order:                        1 !important;
          flex:                         0 0 auto !important;
        }

        p:nth-child(4) {
          order:                        2 !important;
          margin-left:                  auto !important;
          width:                        auto !important;
          .action-group {
            justify-content:            flex-end !important;
          }
        }

        p:nth-child(2) {
          order:                        3 !important;
          flex:                         1 1 100% !important;
        }

        p:nth-child(3) {
          order:                        4 !important;
          flex:                         1 1 100% !important;
        }
      }
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user';
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from 'lightgallery/plugins/zoom'; 
import lgFullscreen from 'lightgallery/plugins/fullscreen';

export default {
  name: 'ExamOthersTab',
  props: {
    exam_id: { type: [String, Number] },
  },
  data: () => ({
    loading: false,
    exam: null,
    examDataList: [],
    session: null,
    isPowerfull: null,
    lightGallerySettings: {
      selector: '.lg-item', 
      download: false,
      counter: true,
      videojs: false,
      plugins: [lgVideo, lgZoom, lgFullscreen]
    }
  }),
  computed: {
    allExamOthersData() {
      return this.examDataList.filter(img => img.type && img.type.startsWith('OTHER_'));
    },
    examOthersMediaData() {
      return this.allExamOthersData.filter(item => this.isSupportedImage(item) || this.isSupportedVideo(item));
    },
    othersTableData() {
      return {
        items: this.allExamOthersData,
        columns: [
          { key: 'icon', name: '', type: 'custom', width: '2%' },
          { name: '', type: 'string', value: 'originalName', width: '39%' },
          { name: '', type: 'string', value: 'description', width: '39%' },
          { key: 'actions', name: '', type: 'custom', width: '20%' }
        ]
      };
    }
  },
  created() {
    const userStore = useUserStore();
    this.session = userStore.sessionInfo;
    this.isPowerfull = this.session.isPowerfull;
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      NProgress.start();
      try {
        let examResponse = await this.$examService.get({ id: this.exam_id });
        this.exam = examResponse.exam;

        let response = await this.$examDataService.list({ id: this.exam.id });
        this.examDataList = response.examDataList;
        
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    async handlePreview(item) {
      if (this.isSupportedImage(item) || this.isSupportedVideo(item)) {
        const anchor = document.getElementById(`lg-item-${item.id}`);
        if (anchor) {
          anchor.click();
        }
      } else {
        const urlFile = this.$examDataService.getDownloadFileUrl({ uuid: item.uuid, inline: true });

        const isPreviewOpened = await this.$refs.baseFilePreviewModal.show({
          url: urlFile,
          file: item
        });

        if (isPreviewOpened === 'unsupported') {
          await this.doDownload(item);
          this.$message.popup(this, 'exam.previewUnavailableDownloadStarted', 'operationWarning', 'warn')
        }
      }
    },
    async doDownload(item) {
      this.loading = true;
      NProgress.start();

      try {
        const url = this.$examDataService.getDownloadFileUrl({ uuid: item.uuid });
        this.$utils.triggerFileDownload(url)
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    async openOffcanvasDelete(deleteId) {
      const ok = await this.$refs.offcanvasGeneric.show({
        modalType: 'danger',
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('exam.confirmFileRemoval'),
        okButton: this.$i18n.t('remove'),
        okButtonClass: 'btn-danger',
        cancelButton: this.$i18n.t('cancel'),
        cancelButtonClass: 'btn-outline-secondary'
      });

      if (ok) {
        this.doDelete(deleteId);
      }
    },
    async doDelete(deleteId) {
      let params = { id: deleteId };
      this.loading = true;
      NProgress.start();

      try {
        await this.$examDataService.remove(params);
        this.$message.popup(this, 'general.removedSuccess', 'operationSuccess', 'success');
        await this.fetchData();
      } catch (err) {
        this.$message.popup(this, 'general.removedError', 'operationFail', 'error');
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },
    isExamDataOthersRemoveAllowed(item) {
      return item.type !== 'OTHER_CONSENT_PDF' && this.isPowerfull;
    },
    getMediaSource(item) {
      return this.$examDataService.getDownloadFileUrl({ uuid: item.uuid });
    },
    getVideoSource(item) {
      const url = this.getMediaSource(item);
      let result = {
        "source": [{ "src": url, "type": "video/mp4" }],
        "attributes": { "preload": false, "playsinline": false, "controls": true, "loop": true }
      }
      return JSON.stringify(result);
    },
    isSupportedVideo(item) {
      return this.$utils.isSupportedGalleryVideo(item?.extension);
    },
    isSupportedImage(item) {
      return this.$utils.isSupportedGalleryImage(item?.extension);
    },
    handleRowClick(payload) {
      if (payload && payload.item) {
        this.handlePreview(payload.item);
      }
    },
  }
}
</script>